import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { Reveal, Label, MaskLines } from "../components/Shared";
import Ribbon from "../components/Ribbon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Full Name", type: "text", required: true, placeholder: "Jane Doe" },
  { name: "company", label: "Company Name", type: "text", required: true, placeholder: "Your Brand, Inc." },
  { name: "email", label: "Email Address", type: "email", required: true, placeholder: "jane@yourbrand.com" },
  { name: "phone", label: "Phone (optional)", type: "tel", required: false, placeholder: "+1 (555) 000-0000" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Inquiry received. We'll be in touch shortly.");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email info@mdlabsca.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="relative overflow-hidden bg-navy pt-40 pb-20 lg:pt-48 lg:pb-24">
        <div className="pointer-events-none absolute inset-0 opacity-[0.1]">
          <div className="mx-auto grid h-full max-w-[1400px] grid-cols-6 px-6 lg:grid-cols-12 lg:px-12">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="border-l border-white/40 last:border-r" />
            ))}
          </div>
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal>
            <Label light>Contact Us</Label>
          </Reveal>
          <h1 className="mt-8 font-heading text-5xl font-extrabold leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl">
            <MaskLines once delay={0.1} lines={["Let's start a", "conversation."]} />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-slate-300">
              Tell us about your formulation, brand and volume expectations. Our team
              reviews every inquiry personally.
            </p>
          </Reveal>
        </div>
      </section>
      <Ribbon dark />

      <section className="bg-[#FAFAFA] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid gap-16 lg:grid-cols-12 lg:gap-24">
            {/* Info column */}
            <div className="lg:col-span-4">
              <Reveal>
                <div className="space-y-10">
                  <div>
                    <Mail className="h-7 w-7 text-clinical" />
                    <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                      Email
                    </div>
                    <a
                      href="mailto:info@mdlabsca.com"
                      data-testid="contact-email-link"
                      className="mt-2 block font-heading text-xl font-bold text-navy transition-colors hover:text-clinical"
                    >
                      info@mdlabsca.com
                    </a>
                  </div>
                  <div>
                    <MapPin className="h-7 w-7 text-clinical" />
                    <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                      Location
                    </div>
                    <p className="mt-2 font-heading text-xl font-bold text-navy">
                      Westlake Village
                      <br />
                      California, USA
                    </p>
                  </div>
                  <div className="border-l-2 border-clinical bg-white p-6">
                    <Clock className="h-6 w-6 text-clinical" />
                    <p className="mt-3 text-[15px] font-medium leading-relaxed text-navy">
                      We typically respond within 1–2 business days.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Form column */}
            <div className="lg:col-span-8">
              {sent ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex h-full min-h-[400px] flex-col items-start justify-center border border-slate-200 bg-white p-12"
                  data-testid="contact-success"
                >
                  <div className="flex h-14 w-14 items-center justify-center bg-navy">
                    <ArrowRight className="h-7 w-7 text-white" />
                  </div>
                  <h2 className="mt-8 font-heading text-3xl font-extrabold text-navy">
                    Thank you — your inquiry is in.
                  </h2>
                  <p className="mt-4 max-w-md text-lg text-slate-500">
                    A member of the MD Labs team will review your message and respond
                    within 1–2 business days.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another"
                    className="mt-8 text-sm font-semibold text-clinical hover:underline"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <Reveal>
                  <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-10">
                    <div className="grid gap-x-10 gap-y-10 sm:grid-cols-2">
                      {FIELDS.map((f) => (
                        <div key={f.name} className="relative">
                          <label
                            htmlFor={f.name}
                            className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400"
                          >
                            {f.label} {f.required && <span className="text-clinical">*</span>}
                          </label>
                          <input
                            id={f.name}
                            type={f.type}
                            value={form[f.name]}
                            onChange={update(f.name)}
                            placeholder={f.placeholder}
                            required={f.required}
                            data-testid={`contact-input-${f.name}`}
                            className="mt-3 w-full border-b border-slate-300 bg-transparent pb-3 text-lg text-navy placeholder:text-slate-300 outline-none transition-colors duration-200 focus:border-b-2 focus:border-navy"
                          />
                        </div>
                      ))}
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400"
                      >
                        Message / Inquiry <span className="text-clinical">*</span>
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        value={form.message}
                        onChange={update("message")}
                        required
                        placeholder="Tell us about your product, formulation and volume..."
                        data-testid="contact-input-message"
                        className="mt-3 w-full resize-none border-b border-slate-300 bg-transparent pb-3 text-lg text-navy placeholder:text-slate-300 outline-none transition-colors duration-200 focus:border-b-2 focus:border-navy"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-button"
                      className="group inline-flex items-center gap-3 bg-navy px-10 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-clinical disabled:opacity-60"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                        </>
                      ) : (
                        <>
                          Send Inquiry
                          <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </form>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
